/**
 * Edit-mode selection helpers — single source of truth for every read,
 * derivation, freeze, and clear on an `EditModeState`.
 *
 * Every selection-touching call site in the store MUST go through this
 * module. Inline `em.selectedFaces.size > 0 ? …` checks and bespoke
 * `new Set(em.selected…)` clones are forbidden (see CLAUDE.md "Edit Mode
 * Anti-Patterns" — face selection must never be inferred from vertex
 * membership, and stale vertex/face indices after a topology change
 * corrupt geometry when reused).
 */

import { computeFaceNormal } from "./faceNormal.ts";
import {
  edgeNeighbors,
  faceNeighbors,
  getAdjacencyCache,
  type ResolvedTopologyOpSelection,
  resolveTopologyOpSelection,
  topologyFaceCount,
  topologyFaceVerts,
  vertexNeighbors,
} from "./topology.ts";
import type { EditModeState, PolygonTopology } from "./types.ts";

// ---------------------------------------------------------------------------
// Predicates — centralised selection "has anything?" checks
// ---------------------------------------------------------------------------

export const hasVertexSelection = (em: EditModeState): boolean =>
  em.selectedVertices.size > 0;

export const hasEdgeSelection = (em: EditModeState): boolean =>
  em.selectedEdges.size > 0;

export const hasFaceSelection = (em: EditModeState): boolean =>
  em.selectedFaces.size > 0;

export const hasAnySelection = (em: EditModeState): boolean =>
  em.selectedVertices.size > 0 ||
  em.selectedEdges.size > 0 ||
  em.selectedFaces.size > 0;

/** True iff we're in face granularity AND at least one face is selected. */
export const isFaceMode = (em: EditModeState): boolean =>
  em.granularity === "face" && hasFaceSelection(em);

/** Total number of selected elements across all three granularities. */
export const selectionCount = (em: EditModeState): number =>
  em.selectedVertices.size + em.selectedEdges.size + em.selectedFaces.size;

// ---------------------------------------------------------------------------
// Selection-construction primitives
// ---------------------------------------------------------------------------

/**
 * Seed set for a new selection pass: a fresh clone of `current` when
 * the user is extending (Shift-click, Shift-box, Shift-loop), or an
 * empty set when they're replacing. Every additive/replace click path
 * must begin here — eliminates the `additive ? new Set(cur) : new Set()`
 * ternary that was spread across 15+ call sites.
 *
 * When the caller wants the full "toggle-or-replace" reducer (single
 * element or batch), prefer {@link toggleSelection} /
 * {@link toggleSelectionBatch} — this helper is the lower-level seed
 * primitive, used for loop/path/box paths that layer on their own
 * accumulation logic.
 */
export function startSelectionSet<T>(
  current: ReadonlySet<T>,
  additive: boolean,
): Set<T> {
  return additive ? new Set(current) : new Set<T>();
}

/**
 * Single-element selection reducer:
 *
 *   • Non-additive click → replace with exactly `{item}`.
 *   • Additive click, `item` already in set → deselect (remove).
 *   • Additive click, `item` absent → extend (add).
 *
 * Centralises the "Shift-click toggles, plain click replaces" idiom used
 * by every granularity's single-element click path (face, edge, vertex).
 * Always returns a fresh set — the input is never mutated.
 */
export function toggleSelection<T>(
  current: ReadonlySet<T>,
  item: T,
  additive: boolean,
): Set<T> {
  if (!additive) return new Set([item]);
  const next = new Set(current);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

/**
 * Batch-element selection reducer — the companion to
 * {@link toggleSelection} for ops that select multiple elements at once
 * (face loop, edge loop, vertex path, box select of a single face):
 *
 *   • Non-additive → replace with exactly `items`.
 *   • Additive, every element already selected → deselect them all.
 *   • Additive, at least one element missing → add every missing one
 *     (partial selections are promoted to fully selected, matching
 *     Blender's loop/path behaviour).
 *
 * Always returns a fresh set — the input is never mutated. `items` is
 * consumed once, so any iterable (including generators) is accepted.
 */
export function toggleSelectionBatch<T>(
  current: ReadonlySet<T>,
  items: Iterable<T>,
  additive: boolean,
): Set<T> {
  if (!additive) return new Set(items);
  const arr = Array.from(items);
  const next = new Set(current);
  const allSelected = arr.every((i) => next.has(i));
  if (allSelected) {
    for (const i of arr) next.delete(i);
  } else {
    for (const i of arr) next.add(i);
  }
  return next;
}

/**
 * Local-space centroid of the vertices identified by `indices` into a
 * flat xyz Float32 array (`[x0,y0,z0, x1,y1,z1, …]`). Returns `null`
 * when the result would be empty.
 *
 * `indices` accepts any iterable (Set, Array, etc.). Omit to average
 * every vertex in the buffer — unifies the "set origin to geometry"
 * and "set origin to selection" centroids under the same helper.
 */
export function vertexCentroid(
  vertices: Float32Array,
  indices?: Iterable<number>,
): [number, number, number] | null {
  let cx = 0,
    cy = 0,
    cz = 0,
    n = 0;
  if (indices === undefined) {
    n = vertices.length / 3;
    if (n === 0) return null;
    for (let i = 0; i < n; i++) {
      cx += vertices[i * 3];
      cy += vertices[i * 3 + 1];
      cz += vertices[i * 3 + 2];
    }
  } else {
    for (const vi of indices) {
      cx += vertices[vi * 3];
      cy += vertices[vi * 3 + 1];
      cz += vertices[vi * 3 + 2];
      n++;
    }
    if (n === 0) return null;
  }
  return [cx / n, cy / n, cz / n];
}

// ---------------------------------------------------------------------------
// Target-set resolution for topology operations
// ---------------------------------------------------------------------------

/**
 * Strict read of `em.selectedFaces` — returns the explicit face set when
 * populated, or `undefined`. Use when you only want to act on faces the
 * user explicitly selected in face mode, with no vertex-inference
 * fallback.
 *
 * CLAUDE.md "Edit Mode Anti-Patterns" forbids inferring face selection
 * from vertex membership, so this is the preferred direction. Most
 * existing topology ops use `facesForOp` (below) for backwards
 * compatibility with vertex-mode workflows — new code should prefer
 * this helper.
 */
export const faceTargetForOp = (em: EditModeState): Set<number> | undefined =>
  em.selectedFaces.size > 0 ? em.selectedFaces : undefined;

/**
 * Resolve the face-target set for topology operations that accept
 * `faces: Set<number> | undefined` and that want graceful vertex-mode
 * fallback: returns the same set `resolveEditModeSelection` would
 * produce (explicit faces in face/edge mode, derived faces in
 * vertex mode), or `undefined` when the result is empty.
 *
 * Entry point for delete/dissolve/extrude/inset/bevel/separate —
 * operations that want "treat this vertex selection as the faces it
 * forms, if it forms any". For strict "explicit face selection only"
 * semantics, use {@link faceTargetForOp} instead.
 */
export function facesForOp(
  topology: PolygonTopology,
  em: EditModeState,
): Set<number> | undefined {
  const { selectedFaces } = resolveEditModeSelection(topology, em);
  return selectedFaces.size > 0 ? selectedFaces : undefined;
}

// ---------------------------------------------------------------------------
// Derivation helpers — face↔vertex set conversion
// ---------------------------------------------------------------------------

/**
 * Derive the set of edge indices touched by `selectedFaces` — the union of
 * each face's boundary edges. Safe direction: explicit face selection remains
 * authoritative and no shared-vertex inference is involved.
 */
export function deriveEdgesFromFaces(
  topology: PolygonTopology,
  selectedFaces: Set<number>,
): Set<number> {
  const edges = new Set<number>();
  const { offsets, cornerEdges } = topology;
  for (const f of selectedFaces) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) edges.add(cornerEdges[start + j]);
  }
  return edges;
}

/**
 * Resolve the authoritative edge set for per-edge attribute operations
 * (mark seam, mark sharp, and future crease / bevel weight ops).
 *
 * Mirrors Blender's N-panel behavior: works from whichever granularity the
 * user is in. Edge mode passes `em.selectedEdges` through verbatim; face
 * mode returns the union of each selected face's boundary edges; vertex
 * mode classifies the vertex selection via the shared
 * {@link resolveEditModeSelection} pipeline (edges whose both endpoints
 * are selected).
 */
export function resolveSelectedEdges(
  topology: PolygonTopology,
  em: EditModeState,
): Set<number> {
  if (em.granularity === "edge") return em.selectedEdges;
  if (em.granularity === "face")
    return deriveEdgesFromFaces(topology, em.selectedFaces);
  return resolveEditModeSelection(topology, em).selectedEdges;
}

/**
 * Derive the union of endpoint vertices for `selectedEdges`.
 *
 * Safe direction: explicit edge selection remains authoritative and no face
 * membership is inferred from shared vertices.
 */
export function deriveVerticesFromEdges(
  topology: PolygonTopology,
  selectedEdges: Set<number>,
): Set<number> {
  const verts = new Set<number>();
  const { edges } = topology;
  for (const ei of selectedEdges) {
    verts.add(edges[ei * 2]);
    verts.add(edges[ei * 2 + 1]);
  }
  return verts;
}

/**
 * Return the endpoint vertex set iff `selectedEdges` forms one exact simple
 * closed loop in the topology.
 *
 * "Exact" means the induced subgraph on the selected vertices contains no
 * extra topology edges beyond the explicit edge selection. This protects
 * vertex-driven boundary operators (for example fill and grid fill) from
 * broadening an edge-mode selection through shared vertices on a face.
 */
export function deriveVerticesFromExactEdgeLoop(
  topology: PolygonTopology,
  selectedEdges: Set<number>,
): Set<number> | undefined {
  if (selectedEdges.size < 3) return undefined;

  const selectedVertices = deriveVerticesFromEdges(topology, selectedEdges);
  if (selectedVertices.size < 3) return undefined;

  const degree = new Map<number, number>();
  const adjacency = new Map<number, Set<number>>();
  for (const vi of selectedVertices) {
    degree.set(vi, 0);
    adjacency.set(vi, new Set());
  }

  const { edges } = topology;
  const edgeCount = edges.length / 2;
  let inducedEdgeCount = 0;

  for (let ei = 0; ei < edgeCount; ei++) {
    const a = edges[ei * 2];
    const b = edges[ei * 2 + 1];
    if (!selectedVertices.has(a) || !selectedVertices.has(b)) continue;
    if (!selectedEdges.has(ei)) return undefined;

    inducedEdgeCount++;
    degree.set(a, (degree.get(a) ?? 0) + 1);
    degree.set(b, (degree.get(b) ?? 0) + 1);
    adjacency.get(a)?.add(b);
    adjacency.get(b)?.add(a);
  }

  if (inducedEdgeCount !== selectedEdges.size) return undefined;
  for (const count of degree.values()) {
    if (count !== 2) return undefined;
  }

  const start = selectedVertices.values().next().value;
  if (start === undefined) return undefined;

  const visited = new Set<number>();
  const stack = [start];
  while (stack.length > 0) {
    const vi = stack.pop()!;
    if (visited.has(vi)) continue;
    visited.add(vi);
    for (const next of adjacency.get(vi) ?? []) {
      if (!visited.has(next)) stack.push(next);
    }
  }

  return visited.size === selectedVertices.size ? selectedVertices : undefined;
}

/**
 * Derive the set of faces whose ENTIRE boundary edge ring is in
 * `selectedEdges`.
 *
 * This is the safe edge→face conversion for granularity switching: unlike
 * vertex-based inference it cannot "light up" a face just because its corner
 * vertices happen to be shared by neighboring selected edges.
 */
export function deriveFacesFromEdges(
  topology: PolygonTopology,
  selectedEdges: Set<number>,
): Set<number> {
  const { offsets, cornerEdges } = topology;
  const faceCount = offsets.length - 1;
  const faces = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    let allSelected = size > 0;
    for (let j = 0; j < size; j++) {
      if (!selectedEdges.has(cornerEdges[start + j])) {
        allSelected = false;
        break;
      }
    }
    if (allSelected) faces.add(f);
  }
  return faces;
}

/**
 * Derive the set of face indices whose corners are ALL present in
 * `selectedVertices`. Use with care — this is the inverse direction
 * that the CLAUDE.md anti-pattern warns about: if two non-adjacent faces
 * share a vertex, neither of them is "selected", but a grid of three
 * faces where the outer two are selected yields the middle one as
 * well. Only call this when the caller semantically wants that
 * classification (vertex-mode operators that need a face set).
 */
export function deriveFacesFromVertices(
  topology: PolygonTopology,
  selectedVertices: Set<number>,
): Set<number> {
  const faceCount = topologyFaceCount(topology);
  const faces = new Set<number>();
  for (let f = 0; f < faceCount; f++) {
    const fv = topologyFaceVerts(topology, f);
    if (fv.every((v) => selectedVertices.has(v))) faces.add(f);
  }
  return faces;
}

/**
 * Derive `selectedVertices` from a set of face indices — the union of
 * all face corner vertex sets. This is the *safe* direction: after a
 * face-mode selection change, rebuild the vertex set from the faces
 * (see CLAUDE.md "Never accumulate selectedVertices independently in
 * face mode").
 */
export function deriveVerticesFromFaces(
  topology: PolygonTopology,
  selectedFaces: Set<number>,
): Set<number> {
  const verts = new Set<number>();
  for (const f of selectedFaces) {
    const fv = topologyFaceVerts(topology, f);
    for (const v of fv) verts.add(v);
  }
  return verts;
}

// ---------------------------------------------------------------------------
// Granularity conversion — single source of truth for mode switching
// ---------------------------------------------------------------------------

export interface GranularitySelection {
  selectedVertices: Set<number>;
  selectedEdges: Set<number>;
  selectedFaces: Set<number>;
}

/**
 * Convert the live selection into the target granularity while preserving the
 * most authoritative source available:
 *
 * - face → edge uses explicit face boundaries, not vertex inference
 * - edge → face uses explicit edge rings, not shared-vertex inference
 * - vertex mode falls back to the existing vertex-driven classification
 *
 * This keeps mode switches from broadening the selection in the same
 * shared-vertex cases that motivated explicit `selectedEdges` /
 * `selectedFaces`.
 */
export function convertSelectionForGranularity(
  topology: PolygonTopology,
  em: EditModeState,
  granularity: EditModeState["granularity"],
): GranularitySelection {
  if (granularity === "vertex") {
    return {
      selectedVertices: new Set(em.selectedVertices),
      selectedEdges: new Set(),
      selectedFaces: new Set(),
    };
  }

  if (granularity === "edge") {
    const selectedEdges =
      em.granularity === "face" && em.selectedFaces.size > 0
        ? deriveEdgesFromFaces(topology, em.selectedFaces)
        : resolveEditModeSelection(topology, em).selectedEdges;
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  const selectedFaces =
    em.granularity === "edge" && em.selectedEdges.size > 0
      ? deriveFacesFromEdges(topology, em.selectedEdges)
      : resolveEditModeSelection(topology, em).selectedFaces;
  return {
    selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
    selectedEdges: new Set(),
    selectedFaces,
  };
}

export interface BoxSelectionPatch {
  selectedVertices: Set<number>;
  selectedEdges: Set<number>;
  selectedFaces: Set<number>;
}

export type BrushSelectionMode = "set" | "add" | "subtract";

export function applyBoxSelection(
  topology: PolygonTopology,
  em: EditModeState,
  incoming: {
    vertices: ReadonlySet<number>;
    edges: ReadonlySet<number>;
    faces: ReadonlySet<number>;
  },
  additive: boolean,
): BoxSelectionPatch {
  if (em.granularity === "face") {
    const selectedFaces = startSelectionSet(em.selectedFaces, additive);
    for (const face of incoming.faces) selectedFaces.add(face);
    return {
      selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
      selectedEdges: new Set(),
      selectedFaces,
    };
  }

  if (em.granularity === "edge") {
    const selectedEdges = startSelectionSet(em.selectedEdges, additive);
    for (const edge of incoming.edges) selectedEdges.add(edge);
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  const selectedVertices = startSelectionSet(em.selectedVertices, additive);
  for (const vertex of incoming.vertices) selectedVertices.add(vertex);
  return {
    selectedVertices,
    selectedEdges: new Set(),
    selectedFaces: new Set(),
  };
}

function applyBrushSet<T>(
  current: ReadonlySet<T>,
  incoming: ReadonlySet<T>,
  mode: BrushSelectionMode,
): Set<T> {
  if (mode === "set") return new Set(incoming);

  const next = new Set(current);
  if (mode === "add") {
    for (const item of incoming) next.add(item);
  } else {
    for (const item of incoming) next.delete(item);
  }
  return next;
}

export function applyBrushSelection(
  topology: PolygonTopology,
  em: EditModeState,
  incoming: {
    vertices: ReadonlySet<number>;
    edges: ReadonlySet<number>;
    faces: ReadonlySet<number>;
  },
  mode: BrushSelectionMode,
): BoxSelectionPatch {
  if (em.granularity === "face") {
    const selectedFaces = applyBrushSet(em.selectedFaces, incoming.faces, mode);
    return {
      selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
      selectedEdges: new Set(),
      selectedFaces,
    };
  }

  if (em.granularity === "edge") {
    const selectedEdges = applyBrushSet(em.selectedEdges, incoming.edges, mode);
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  const selectedVertices = applyBrushSet(
    em.selectedVertices,
    incoming.vertices,
    mode,
  );
  return {
    selectedVertices,
    selectedEdges: new Set(),
    selectedFaces: new Set(),
  };
}

function floodFillSelection(
  seeds: ReadonlySet<number>,
  neighborLookup: (index: number) => readonly number[],
): Set<number> {
  const connected = new Set<number>();
  const queue = [...seeds];
  while (queue.length > 0) {
    const current = queue.pop()!;
    if (connected.has(current)) continue;
    connected.add(current);
    for (const neighbor of neighborLookup(current)) {
      if (!connected.has(neighbor)) queue.push(neighbor);
    }
  }
  return connected;
}

function firstSelectedByStorageOrder(
  selected: ReadonlySet<number>,
): number | null {
  let first: number | null = null;
  for (const item of selected) {
    if (first == null || item < first) first = item;
  }
  return first;
}

function resolveCheckerSeed(
  selected: ReadonlySet<number>,
  active: number | null,
): number | null {
  if (active != null && selected.has(active)) return active;
  return firstSelectedByStorageOrder(selected);
}

function checkerDeselectIsland(
  seed: number,
  selectedNeighbors: (index: number) => readonly number[],
): { island: Set<number>; keep: Set<number> } {
  const island = new Set<number>();
  const keep = new Set<number>();
  const parity = new Map<number, 0 | 1>([[seed, 0]]);
  const queue = [seed];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (island.has(current)) continue;
    island.add(current);

    const currentParity = parity.get(current) ?? 0;
    if (currentParity === 0) keep.add(current);

    const nextParity: 0 | 1 = currentParity === 0 ? 1 : 0;
    const neighbors = [...selectedNeighbors(current)].sort((a, b) => a - b);
    for (const neighbor of neighbors) {
      if (!parity.has(neighbor)) parity.set(neighbor, nextParity);
      queue.push(neighbor);
    }
  }

  return { island, keep };
}

function checkerDeselectLinearSelection(
  selected: ReadonlySet<number>,
  seed: number,
): { island: Set<number>; keep: Set<number> } {
  const ordered = [...selected].sort((a, b) => a - b);
  const seedIndex = ordered.indexOf(seed);
  if (seedIndex < 0) return { island: new Set(), keep: new Set() };

  const keep = new Set<number>();
  for (let i = 0; i < ordered.length; i++) {
    if (i % 2 === seedIndex % 2) keep.add(ordered[i]);
  }
  return { island: new Set(ordered), keep };
}

export function selectConnectedEditSelection(
  topology: PolygonTopology,
  vertexCount: number,
  em: EditModeState,
): GranularitySelection | null {
  const cache = getAdjacencyCache(topology, vertexCount);

  if (em.granularity === "face") {
    const seeds =
      em.selectedFaces.size > 0
        ? em.selectedFaces
        : resolveEditModeSelection(topology, em).selectedFaces;
    if (seeds.size === 0) return null;
    const selectedFaces = floodFillSelection(seeds, (face) =>
      faceNeighbors(cache, topology, face),
    );
    return {
      selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
      selectedEdges: new Set(),
      selectedFaces,
    };
  }

  if (em.granularity === "edge") {
    const seeds =
      em.selectedEdges.size > 0
        ? em.selectedEdges
        : resolveEditModeSelection(topology, em).selectedEdges;
    if (seeds.size === 0) return null;
    const selectedEdges = floodFillSelection(seeds, (edge) =>
      edgeNeighbors(cache, topology, edge),
    );
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  if (em.selectedVertices.size === 0) return null;
  const selectedVertices = floodFillSelection(em.selectedVertices, (vertex) =>
    vertexNeighbors(cache, topology, vertex),
  );
  return {
    selectedVertices,
    selectedEdges: new Set(),
    selectedFaces: deriveFacesFromVertices(topology, selectedVertices),
  };
}

export function checkerDeselectEditSelection(
  topology: PolygonTopology | undefined,
  vertexCount: number,
  em: EditModeState,
): GranularitySelection | null {
  const cache = topology ? getAdjacencyCache(topology, vertexCount) : null;

  if (em.granularity === "face") {
    if (!topology || !cache) return null;
    const sourceFaces =
      em.selectedFaces.size > 0
        ? em.selectedFaces
        : resolveEditModeSelection(topology, em).selectedFaces;
    if (sourceFaces.size < 2) return null;

    const seed = resolveCheckerSeed(sourceFaces, em.lastActiveFace);
    if (seed == null) return null;

    const { island, keep } = checkerDeselectIsland(seed, (face) =>
      faceNeighbors(cache, topology, face).filter((neighbor) =>
        sourceFaces.has(neighbor),
      ),
    );
    const selectedFaces = new Set(sourceFaces);
    for (const face of island) selectedFaces.delete(face);
    for (const face of keep) selectedFaces.add(face);
    return {
      selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
      selectedEdges: new Set(),
      selectedFaces,
    };
  }

  if (em.granularity === "edge") {
    if (!topology || !cache) return null;
    const sourceEdges =
      em.selectedEdges.size > 0
        ? em.selectedEdges
        : resolveEditModeSelection(topology, em).selectedEdges;
    if (sourceEdges.size < 2) return null;

    const seed = resolveCheckerSeed(sourceEdges, em.lastActiveEdge);
    if (seed == null) return null;

    const { island, keep } = checkerDeselectIsland(seed, (edge) =>
      edgeNeighbors(cache, topology, edge).filter((neighbor) =>
        sourceEdges.has(neighbor),
      ),
    );
    const selectedEdges = new Set(sourceEdges);
    for (const edge of island) selectedEdges.delete(edge);
    for (const edge of keep) selectedEdges.add(edge);
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  if (em.selectedVertices.size < 2) return null;

  const seed = resolveCheckerSeed(em.selectedVertices, em.lastActiveVertex);
  if (seed == null) return null;

  const { island, keep } =
    topology && cache
      ? checkerDeselectIsland(seed, (vertex) =>
          vertexNeighbors(cache, topology, vertex).filter((neighbor) =>
            em.selectedVertices.has(neighbor),
          ),
        )
      : checkerDeselectLinearSelection(em.selectedVertices, seed);
  const selectedVertices = new Set(em.selectedVertices);
  for (const vertex of island) selectedVertices.delete(vertex);
  for (const vertex of keep) selectedVertices.add(vertex);
  return {
    selectedVertices,
    selectedEdges: new Set(),
    selectedFaces: new Set(),
  };
}

export function growEditSelection(
  topology: PolygonTopology,
  vertexCount: number,
  em: EditModeState,
): GranularitySelection | null {
  const cache = getAdjacencyCache(topology, vertexCount);

  if (em.granularity === "face") {
    const selectedFaces =
      em.selectedFaces.size > 0
        ? new Set(em.selectedFaces)
        : new Set(resolveEditModeSelection(topology, em).selectedFaces);
    if (selectedFaces.size === 0) return null;
    for (const face of [...selectedFaces]) {
      for (const neighbor of faceNeighbors(cache, topology, face)) {
        selectedFaces.add(neighbor);
      }
    }
    return {
      selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
      selectedEdges: new Set(),
      selectedFaces,
    };
  }

  if (em.granularity === "edge") {
    const selectedEdges =
      em.selectedEdges.size > 0
        ? new Set(em.selectedEdges)
        : new Set(resolveEditModeSelection(topology, em).selectedEdges);
    if (selectedEdges.size === 0) return null;
    for (const edge of [...selectedEdges]) {
      for (const neighbor of edgeNeighbors(cache, topology, edge)) {
        selectedEdges.add(neighbor);
      }
    }
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  if (em.selectedVertices.size === 0) return null;
  const selectedVertices = new Set(em.selectedVertices);
  for (const vertex of em.selectedVertices) {
    for (const neighbor of vertexNeighbors(cache, topology, vertex)) {
      selectedVertices.add(neighbor);
    }
  }
  return {
    selectedVertices,
    selectedEdges: new Set(),
    selectedFaces: new Set(),
  };
}

export function shrinkEditSelection(
  topology: PolygonTopology,
  vertexCount: number,
  em: EditModeState,
): GranularitySelection | null {
  const cache = getAdjacencyCache(topology, vertexCount);

  if (em.granularity === "face") {
    const sourceFaces =
      em.selectedFaces.size > 0
        ? em.selectedFaces
        : resolveEditModeSelection(topology, em).selectedFaces;
    if (sourceFaces.size === 0) return null;
    const selectedFaces = new Set<number>();
    for (const face of sourceFaces) {
      const neighbors = faceNeighbors(cache, topology, face);
      if (neighbors.every((neighbor) => sourceFaces.has(neighbor))) {
        selectedFaces.add(face);
      }
    }
    return {
      selectedVertices: deriveVerticesFromFaces(topology, selectedFaces),
      selectedEdges: new Set(),
      selectedFaces,
    };
  }

  if (em.granularity === "edge") {
    const sourceEdges =
      em.selectedEdges.size > 0
        ? em.selectedEdges
        : resolveEditModeSelection(topology, em).selectedEdges;
    if (sourceEdges.size === 0) return null;
    const selectedEdges = new Set<number>();
    for (const edge of sourceEdges) {
      const neighbors = edgeNeighbors(cache, topology, edge);
      if (neighbors.every((neighbor) => sourceEdges.has(neighbor))) {
        selectedEdges.add(edge);
      }
    }
    return {
      selectedVertices: deriveVerticesFromEdges(topology, selectedEdges),
      selectedEdges,
      selectedFaces: new Set(),
    };
  }

  if (em.selectedVertices.size === 0) return null;
  const selectedVertices = new Set<number>();
  for (const vertex of em.selectedVertices) {
    const neighbors = vertexNeighbors(cache, topology, vertex);
    if (neighbors.every((neighbor) => em.selectedVertices.has(neighbor))) {
      selectedVertices.add(vertex);
    }
  }
  return {
    selectedVertices,
    selectedEdges: new Set(),
    selectedFaces: new Set(),
  };
}

// ---------------------------------------------------------------------------
// Hide/unhide reconciliation — keep hiddenVerts consistent with hiddenFaces
// ---------------------------------------------------------------------------

/**
 * Expand `hiddenFaces` to every face that touches a vertex already in
 * `hiddenVerts` — the "a vertex being hidden hides all its fans"
 * propagation direction. Used by vertex/edge-mode hide where the user
 * picks vertices and we have to decide which faces go dark with them.
 *
 * Mutates `hiddenFaces` in place.
 */
export function propagateHiddenVerticesToFaces(
  topology: PolygonTopology,
  hiddenVerts: ReadonlySet<number>,
  hiddenFaces: Set<number>,
): void {
  const faceCount = topologyFaceCount(topology);
  for (let fi = 0; fi < faceCount; fi++) {
    if (hiddenFaces.has(fi)) continue;
    const verts = topologyFaceVerts(topology, fi);
    if (verts.some((vi) => hiddenVerts.has(vi))) hiddenFaces.add(fi);
  }
}

/**
 * Reconcile `hiddenVerts` with `hiddenFaces` — every vertex on a
 * hidden face is itself hidden, and every vertex that still belongs
 * to at least one visible face becomes visible (single pass for the
 * add, single pass for the remove).
 *
 * Single source of truth for the "faces drive visibility" pattern
 * shared by `hideEditSelection` and `hideEditUnselected` — previously
 * copy-pasted across four branches in `selectionOps.ts`. Mutates
 * `hiddenVerts` in place.
 */
export function syncHiddenVerticesToFaces(
  topology: PolygonTopology,
  hiddenFaces: ReadonlySet<number>,
  hiddenVerts: Set<number>,
): void {
  const faceCount = topologyFaceCount(topology);
  for (let fi = 0; fi < faceCount; fi++) {
    if (hiddenFaces.has(fi)) {
      for (const vi of topologyFaceVerts(topology, fi)) hiddenVerts.add(vi);
    }
  }
  for (let fi = 0; fi < faceCount; fi++) {
    if (!hiddenFaces.has(fi)) {
      for (const vi of topologyFaceVerts(topology, fi)) hiddenVerts.delete(vi);
    }
  }
}

/**
 * Single canonical resolver for topology-op selection sets given an
 * EditModeState. Dispatches on granularity and honours explicit
 * edge/face selection when populated; falls back to vertex-inference
 * for vertex-mode operators.
 *
 * Replaces the older private `resolveEditTopologySelection` wrapper —
 * callers should use this function directly.
 */
export function resolveEditModeSelection(
  topology: PolygonTopology,
  em: EditModeState,
): ResolvedTopologyOpSelection {
  return resolveTopologyOpSelection(
    topology,
    em.selectedVertices,
    em.granularity,
    em.selectedEdges,
    em.selectedFaces,
  );
}

// ---------------------------------------------------------------------------
// State mutation helpers — freeze / clear
//
// Two selection snapshot shapes live here. They overlap in fields but have
// distinct lifetimes and consumers — don't unify them.
//
//   freezeEditSelection → FrozenEditSelection
//     In-session: stashed inside `CommandBuffer` on command entry (inset,
//     bevel, edge loop insert, edge slide, transform with proportional
//     edit). Carries the three selection sets + `lastActive*` so a
//     cancel/undo mid-command restores exactly what the user had when they
//     pressed I / Ctrl+B / Ctrl+R. Consumed while still in edit mode on the
//     same object, so `objectId`/`granularity` don't need to round-trip.
//
//   captureEditSelectionForRecall → RecallableEditSelection
//     Persists past mode exit: stored on `AppState.lastEditModeSelection`
//     so `recallSelection` can re-apply the prior selection after a
//     replace-style click clobbers it or the user escapes out of edit mode
//     and comes back. Needs `objectId` + `granularity` because neither is
//     guaranteed to match the live `EditModeState` when the record is
//     consumed. `lastActive*` is intentionally NOT carried — those markers
//     are ephemeral adjacency hints, not part of the user-visible selection.
// ---------------------------------------------------------------------------

export interface FrozenEditSelection {
  preEditSelection: Set<number>;
  preEditEdges: Set<number>;
  preEditFaces: Set<number>;
  preEditActiveVertex: number | null;
  preEditActiveEdge: number | null;
  preEditActiveFace: number | null;
}

/**
 * Snapshot the current edit selection for stashing into a command
 * buffer or undo record. Every set is cloned — the returned frozen
 * state is independent of the live `EditModeState`.
 *
 * Use at command-buffer entry points (inset, bevel, edge loop insert,
 * edge slide, etc.) so a cancel/undo can restore the exact selection
 * the user had when the command started.
 */
export function freezeEditSelection(em: EditModeState): FrozenEditSelection {
  return {
    preEditSelection: new Set(em.selectedVertices),
    preEditEdges: new Set(em.selectedEdges),
    preEditFaces: new Set(em.selectedFaces),
    preEditActiveVertex: em.lastActiveVertex,
    preEditActiveEdge: em.lastActiveEdge,
    preEditActiveFace: em.lastActiveFace,
  };
}

/**
 * Recall record shape written to `lastEditModeSelection` — independent
 * clones of the three selection sets keyed by the object id + active
 * granularity so `recallSelection` can re-apply exactly the state the
 * user had before the clear/replace.
 */
export interface RecallableEditSelection {
  objectId: string;
  selectedVertices: Set<number>;
  selectedEdges: Set<number>;
  selectedFaces: Set<number>;
  granularity: EditModeState["granularity"];
}

/**
 * Snapshot the live edit selection into the shape
 * `AppState.lastEditModeSelection` expects. Every set is cloned.
 *
 * Use wherever selection recall is captured: before a replace-style
 * click, before Escape-clearing the selection, etc.
 */
export function captureEditSelectionForRecall(
  em: EditModeState,
): RecallableEditSelection {
  return {
    objectId: em.objectId,
    selectedVertices: new Set(em.selectedVertices),
    selectedEdges: new Set(em.selectedEdges),
    selectedFaces: new Set(em.selectedFaces),
    granularity: em.granularity,
  };
}

/**
 * Return an EditModeState with every selection- and hidden-set cleared.
 *
 * Use after topology-changing operations (delete, dissolve, subdivide,
 * bridge, fill, inset, bevel, merge, …) — CLAUDE.md requires a clean
 * slate because the operation invalidates vertex/face indices, and
 * stale hidden/selected sets would silently corrupt geometry if
 * reused.
 *
 * `lastActive*` is deliberately NOT cleared here — the "last clicked"
 * markers for shortest-path selection can survive topology changes
 * (they resolve against the new mesh on next use).
 */
export function clearEditSelection(em: EditModeState): EditModeState {
  return {
    ...em,
    selectedVertices: new Set(),
    selectedEdges: new Set(),
    selectedFaces: new Set(),
    hiddenVertices: new Set(),
    hiddenFaces: new Set(),
  };
}

/**
 * Tolerance applied to the cosine comparison so that floating-point noise on
 * normalized vectors does not cause same-facing faces to miss the threshold.
 * Sized comfortably above float32 round-off for unit 3-vectors and well below
 * any threshold reachable from the redo panel's 1° step.
 */
const NORMAL_MATCH_COS_EPS = 1e-7;

/**
 * Expand a face seed selection by including every non-hidden face whose unit
 * normal is within `thresholdDegrees` of any seed face's unit normal.
 *
 * Pure: takes the seed and produces a new Set, never mutates inputs. The
 * comparison is directional (Blender's "Select Similar > Normal" mode):
 * opposite faces of a cube don't match at small thresholds.
 *
 * Degenerate seeds contribute no comparison vector but are still preserved
 * in the result (idempotency under the redo-panel slider).
 */
export function selectSimilarFacesByNormal(
  vertices: Float32Array,
  topology: PolygonTopology,
  seedFaces: ReadonlySet<number>,
  hiddenFaces: ReadonlySet<number>,
  thresholdDegrees: number,
): Set<number> {
  const out = new Set<number>(seedFaces);

  const seedNormals: [number, number, number][] = [];
  for (const f of seedFaces) {
    if (hiddenFaces.has(f)) continue;
    const n = computeFaceNormal(vertices, topology, f);
    if (n) seedNormals.push(n);
  }
  if (seedNormals.length === 0) return out;

  const clampedDeg = Math.max(0, Math.min(180, thresholdDegrees));
  const cosT = Math.cos((clampedDeg * Math.PI) / 180);
  const cosTEff = cosT - NORMAL_MATCH_COS_EPS;

  const faceCount = topologyFaceCount(topology);
  const tmp: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < faceCount; c++) {
    if (out.has(c) || hiddenFaces.has(c)) continue;
    const n = computeFaceNormal(vertices, topology, c, tmp);
    if (!n) continue;
    for (const s of seedNormals) {
      if (n[0] * s[0] + n[1] * s[1] + n[2] * s[2] >= cosTEff) {
        out.add(c);
        break;
      }
    }
  }
  return out;
}

/**
 * Null any `lastActive*` marker that no longer references a valid element
 * in the current topology. Markers are intentionally preserved across
 * topology ops (see `clearEditSelection`) because they're semantic labels
 * for shortest-path selection, but a mesh that shrank can leave them
 * pointing past the end of the vertex/edge/face arrays. Adjacency
 * lookups are already safe against that (empty subarray fallback), but
 * returning a validated `EditModeState` makes the invariant legible at
 * every read site.
 *
 * Returns `em` unchanged when every marker is either null or in-bounds.
 */
export function sanitizeLastActiveMarkers(
  em: EditModeState,
  topology: PolygonTopology,
  vertexCount: number,
): EditModeState {
  const edgeCount = topology.edges.length / 2;
  const faceCount = topologyFaceCount(topology);
  const vOk = em.lastActiveVertex == null || em.lastActiveVertex < vertexCount;
  const eOk = em.lastActiveEdge == null || em.lastActiveEdge < edgeCount;
  const fOk = em.lastActiveFace == null || em.lastActiveFace < faceCount;
  if (vOk && eOk && fOk) return em;
  return {
    ...em,
    lastActiveVertex: vOk ? em.lastActiveVertex : null,
    lastActiveEdge: eOk ? em.lastActiveEdge : null,
    lastActiveFace: fOk ? em.lastActiveFace : null,
  };
}
